import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import { AuthService, BreadcrumbService, ProductNameService, StateService } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { ViewControls } from 'src/app/shared/models/view-controls';
import { ListRenderedOutputsRequest, ListRenderedOutputsResponse, ProductName, RenderedOutputStatus, RenderedOutputSummary, SetRenderedOutputStatusRequest, SetRenderedOutputStatusResponse, SituViewWebApiClient } from 'situview-webapi-client';
import { ActivatedRoute, Route, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { COMMA, ENTER } from '@angular/cdk/keycodes';

@Component({
  selector: 'rendered-output-list',
  templateUrl: './rendered-output-list.component.html',
  styleUrls: ['./rendered-output-list.component.scss']
})
export class RenderedOutputListComponent extends SituBaseComponent implements OnInit {
  viewControls = new ViewControls(25,0,0,[25, 50, 100]);

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  productName = ProductName;
  status = RenderedOutputStatus;

  // Query parameter constants
  readonly ProductionOnlyParamName: string = "liveOnly";
  readonly ProductTypeParamName: string = "productType";
  readonly StatusParamName: string = "status";
  readonly CreatedByParamName: string = "createdBy";
  readonly SearchParamName: string = "search";
  readonly MyOutputsParamName: string = "myOutputs";
  readonly PageParamName: string = "page";
  readonly PageSizeParamName: string = "pageSize";

  // Filter properties
  isProductionOnly: boolean = false;
  productTypeFilter: ProductName | undefined;
  statusFilter: RenderedOutputStatus | undefined;
  createdByFilter: string | undefined;
  myOutputsFilter: boolean = false;
  previousSearchTerm = "";
  searchTerm = "";
  dataSource = new MatTableDataSource<RenderedOutputSummary>();

  // Chip input configuration
  readonly separatorKeysCodes = [ENTER, COMMA] as const;

  // Cached filter options
  productTypeOptions: { value: ProductName, label: string }[] = [];
  statusOptions: { value: RenderedOutputStatus, label: string }[] = [];

  // Computed properties for filter counts
  get totalActiveFilters(): number {
    let count = 0;
    if (this.searchTerm) count++;
    if (this.isProductionOnly) count++;
    if (this.productTypeFilter !== undefined) count++;
    if (this.statusFilter !== undefined) count++;
    if (this.createdByFilter) count++;
    if (this.myOutputsFilter) count++;
    return count;
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private breadcrumbService: BreadcrumbService,
    private situViewClient: SituViewWebApiClient,
    private productNameService: ProductNameService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar) {
      super(authService, stateService);
      this.loadFiltersFromParams();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.initializeFilterOptions();
  }

  private initializeFilterOptions(): void {
    this.productTypeOptions = this.getProductTypeOptions();
    this.statusOptions = this.getStatusOptions();
  }

  protected onSelectedCompanyChange(): void {
    this.loadRenderedOutputs();
  }

  toggleIsProductionOnly() {
    this.isProductionOnly = !this.isProductionOnly;
    this.updateParams();
    this.loadRenderedOutputs();
  }


  loadRenderedOutputs() {
    // Prevent multiple concurrent requests and ensure we have a company
    if (this.loading) {
      return;
    }
    
    if (!this.selectedCompany?.id) {
      return;
    }

    this.loading = true;
    
    let request = new ListRenderedOutputsRequest();
    request.companyId = this.selectedCompany?.id;
    request.pageNumber = this.viewControls.pageIndex + 1;
    request.pageSize = this.viewControls.pageSize;
    
    // Apply filters
    if (this.isProductionOnly) request.isProduction = true;
    if (this.statusFilter !== undefined) {
      request.status = this.statusFilter;
    }
    if (this.productTypeFilter !== undefined) {
      request.productName = this.productTypeFilter;
    }
    if (this.searchTerm) request.containsWords = [this.searchTerm];

    this.situViewClient.listRenderedOutputs(request)
      .then((response: ListRenderedOutputsResponse) => {
        if (response.success && response?.renderedOutputs) {
          let filteredData = response.renderedOutputs;
          
          // Apply client-side filters that aren't supported by the API
          if (this.createdByFilter) {
            filteredData = filteredData.filter(item => 
              item.createdByName?.toLowerCase().includes(this.createdByFilter!.toLowerCase())
            );
          }
          
          if (this.myOutputsFilter) {
            // For "My Outputs" filter, we'll filter by the current user's name
            // since we don't have direct access to the user ID in RenderedOutputSummary
            const currentUserName = this.authService.getUserName();
            if (currentUserName) {
              filteredData = filteredData.filter(item => 
                item.createdByName?.toLowerCase().includes(currentUserName.toLowerCase())
              );
            }
          }
          
          this.dataSource.data = filteredData;
          this.viewControls.totalItems = response.totalItems ?? 0;
        } else {
          this.dataSource.data = [];
          this.viewControls.totalItems = 0;
        }
      })
      .catch((error: any) => {
        this.dataSource.data = [];
        this.viewControls.totalItems = 0;
        this.showMessage("Sorry! There was an issue retrieving your renders. Please try again.");
      })
      .finally(() => {
        this.loading = false;
      });
  }

  loadFiltersFromParams() {
    const params = this.route.snapshot.queryParams;

    this.searchTerm = params[this.SearchParamName] || '';
    this.isProductionOnly = params[this.ProductionOnlyParamName] === 'true';
    this.myOutputsFilter = params[this.MyOutputsParamName] === 'true';

    if (params[this.ProductTypeParamName]) {
      const productTypeValue = parseInt(params[this.ProductTypeParamName]);
      if (!isNaN(productTypeValue) && Object.values(ProductName).includes(productTypeValue)) {
        this.productTypeFilter = productTypeValue as ProductName;
      }
    }

    if (params[this.StatusParamName]) {
      const statusValue = parseInt(params[this.StatusParamName]);
      if (!isNaN(statusValue) && Object.values(RenderedOutputStatus).includes(statusValue)) {
        this.statusFilter = statusValue as RenderedOutputStatus;
      }
    }

    if (params[this.CreatedByParamName]) {
      this.createdByFilter = params[this.CreatedByParamName];
    }

    if (params[this.PageParamName]) {
      const pageValue = parseInt(params[this.PageParamName]);
      if (!isNaN(pageValue) && pageValue > 0) {
        this.viewControls.pageIndex = pageValue - 1;
      }
    }

    if (params[this.PageSizeParamName]) {
      const pageSizeValue = parseInt(params[this.PageSizeParamName]);
      if (!isNaN(pageSizeValue) && this.viewControls.pageSizeOptions.includes(pageSizeValue)) {
        this.viewControls.pageSize = pageSizeValue;
      }
    }
  }

  updateParams() {
    const queryParams: { [key: string]: any } = {};

    // Only add parameters that have values
    if (this.searchTerm?.trim()) {
      queryParams[this.SearchParamName] = this.searchTerm.trim();
    }
    if (this.isProductionOnly) {
      queryParams[this.ProductionOnlyParamName] = 'true';
    }
    if (this.productTypeFilter !== undefined) {
      queryParams[this.ProductTypeParamName] = this.productTypeFilter.toString();
    }
    if (this.statusFilter !== undefined) {
      queryParams[this.StatusParamName] = this.statusFilter.toString();
    }
    if (this.createdByFilter?.trim()) {
      queryParams[this.CreatedByParamName] = this.createdByFilter.trim();
    }
    if (this.myOutputsFilter) {
      queryParams[this.MyOutputsParamName] = 'true';
    }
    if (this.viewControls.pageIndex > 0) {
      queryParams[this.PageParamName] = (this.viewControls.pageIndex + 1).toString();
    }
    if (this.viewControls.pageSize !== 25) {
      queryParams[this.PageSizeParamName] = this.viewControls.pageSize.toString();
    }

    // Use replaceUrl to avoid creating history entries for every filter change
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      replaceUrl: true
    });
  }

  // Filter methods
  onProductTypeFilterChange(productType: ProductName | undefined) {
    this.productTypeFilter = productType;
    this.resetPagination();
    this.updateParams();
    this.loadRenderedOutputs();
  }

  onStatusFilterChange(status: RenderedOutputStatus | undefined) {
    this.statusFilter = status;
    this.resetPagination();
    this.updateParams();
    this.loadRenderedOutputs();
  }

  onCreatedByFilterChange(createdBy: string) {
    this.createdByFilter = createdBy || undefined;
    this.resetPagination();
    this.updateParams();
    this.loadRenderedOutputs();
  }

  toggleMyOutputs() {
    this.myOutputsFilter = !this.myOutputsFilter;
    this.resetPagination();
    this.updateParams();
    this.loadRenderedOutputs();
  }

  onSearchChange() {
    if (this.searchTerm !== this.previousSearchTerm) {
      this.previousSearchTerm = this.searchTerm;
      this.resetPagination();
      this.updateParams();
      this.loadRenderedOutputs();
    }
  }

  // Clear filter methods
  clearProductTypeFilter() {
    this.onProductTypeFilterChange(undefined);
  }

  clearStatusFilter() {
    this.onStatusFilterChange(undefined);
  }

  clearCreatedByFilter() {
    this.onCreatedByFilterChange('');
  }

  clearSearchFilter() {
    this.searchTerm = '';
    this.onSearchChange();
  }

  clearAllFilters() {
    this.searchTerm = '';
    this.productTypeFilter = undefined;
    this.statusFilter = undefined;
    this.createdByFilter = undefined;
    this.myOutputsFilter = false;
    this.isProductionOnly = false;
    this.resetPagination();
    this.updateParams();
    this.loadRenderedOutputs();
  }

  resetPagination() {
    this.viewControls.pageIndex = 0;
  }

  // Helper methods
  getProductTypeName(productType: ProductName): string {
    return this.productNameService.getProductNameConfiguration(productType)?.product || ProductName[productType];
  }

  getStatusName(status: RenderedOutputStatus): string {
    switch (status) {
      case RenderedOutputStatus.Complete: return 'Complete';
      case RenderedOutputStatus.InProgress: return 'In Progress';
      case RenderedOutputStatus.Queued: return 'Queued';
      case RenderedOutputStatus.Failed: return 'Failed';
      case RenderedOutputStatus.Cancelled: return 'Cancelled';
      default: return 'Unknown';
    }
  }

  getProductTypeOptions(): { value: ProductName, label: string }[] {
    return Object.keys(ProductName)
      .filter(key => isNaN(Number(key)))
      .map(key => ({
        value: ProductName[key as keyof typeof ProductName] as ProductName,
        label: this.productNameService.getProductNameConfiguration(ProductName[key as keyof typeof ProductName])?.product || key
      }));
  }

  getStatusOptions(): { value: RenderedOutputStatus, label: string }[] {
    return Object.keys(RenderedOutputStatus)
      .filter(key => isNaN(Number(key)))
      .map(key => ({
        value: RenderedOutputStatus[key as keyof typeof RenderedOutputStatus] as RenderedOutputStatus,
        label: this.getStatusName(RenderedOutputStatus[key as keyof typeof RenderedOutputStatus])
      }));
  }

  cancelRenderedOutput(renderedOutput: RenderedOutputSummary) {
    if (!renderedOutput) {
      return;
    }
    this.loading = true;

    let request = new SetRenderedOutputStatusRequest();
    request.renderedOutputId = renderedOutput.artifactId;
    request.status = RenderedOutputStatus.Cancelled;

    this.situViewClient.setRenderedOutputStatus(request)
      .then((response: SetRenderedOutputStatusResponse) => {
        if (response.success) {
          let index = this.dataSource.data.findIndex(x => x.artifactId === renderedOutput.artifactId);
          if (index !== -1) {
            this.dataSource.data[index].status = RenderedOutputStatus.Cancelled;
          }
        } else {
          this.showMessage("Sorry! There was an issue at our end cancelling this rendered output. Please try again.");
        }
      })
      .catch((x: any) => {
        this.showMessage("Sorry! There was an issue at our end cancelling this rendered output. Please try again.");
      })
      .finally(() => {
        this.loading = false;
      });
  }

  getProductColour(productName: ProductName) {
    return this.productNameService.getProductNameConfiguration(productName as any)?.productColor;
  }

  getStatusIconAndClass(status: RenderedOutputStatus) {
    switch (status) {
      case RenderedOutputStatus.Complete:
        return { icon: "check_circle", class: "text-success", description: "Completed" };
      case RenderedOutputStatus.InProgress:
        return { icon: "pending", class: "text-info", description: "In Progress" };
      case RenderedOutputStatus.Queued:
        return { icon: "pending", class: "text-muted", description: "Queued" };
      case RenderedOutputStatus.Failed:
        return { icon: "error", class: "text-danger", description: "Failed" };
      case RenderedOutputStatus.Cancelled:
        return { icon: "cancel", class: "text-muted", description: "Cancelled" };
    }
  }

  onPageChange(event: PageEvent) {
    this.viewControls.pageSize = event.pageSize;
    this.viewControls.pageIndex = event.pageIndex;

    this.loadRenderedOutputs();
  }

  resetSearch() {
    this.viewControls.pageIndex = 0;
    this.viewControls.pageSize = 25;
    this.viewControls.totalItems = 0;
  }

  viewRecipeEventSummaryDetail(artifactId: string) {
    this.router.navigate(['/admin/recipe-event-summary', artifactId]);
  }

  isOlderThan2Minutes(date: Date) {
    const twoMinutesAgo = new Date(Date.now() - 2 * 60 * 1000); // 2 minutes in milliseconds
    return date < twoMinutesAgo;
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}
